import { useCallback, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { getDistance, getStoreCoordinates, getStores, useStoreLocator } from '@srcDS/hooks/useStoreLocator';
import { getCurrentLocale } from '@srcDS/utils/getCurrentLocale';
import { getCountryFullName } from '@srcDS/utils/getCountryFullName';
import { useCartContext } from '@srcDS/provider/cart/CartContext';
import { useScrollLock } from '@srcDS/utils/useLockScroll';

export const useFindStore = ({
  isNewVersionOfFindStore = false,
  showDrawer,
  selectedStore,
  selectedSize,
  activeOptions,
  onChangeVariant,
  setStoreList = null,
}) => {
  const [emptyError, setEmptyError] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [postCodeValue, setPostCodeValue] = useState(null);
  const [hasAnyProductInCart, setHasAnyProductInCart] = useState(null);
  const [filteredStores, setFilteredStores] = useState([]);

  const isMobile = useMedia('(max-width: 767px)', false);
  const currentCountry = getCurrentLocale();
  const { cartData } = useCartContext();

  const { getUserPosition, userPosition, preferredPostcode, getPostCode, handlePostcodeChange, loading } =
    useStoreLocator();

  useScrollLock(showDrawer);

  useEffect(() => {
    setLocationError(false);
    if (userPosition) {
      getPostCode(userPosition);
    }
  }, [userPosition]);

  useEffect(() => {
    if (isNewVersionOfFindStore) {
      if (selectedStore?.skuId && cartData?.cart?.lineItems?.length) {
        setHasAnyProductInCart(true);
      }
    }
  }, [isNewVersionOfFindStore, selectedSize, cartData]);

  const handleFilterStores = async (postcode, setEmptyError, setFilteredStores, sku) => {
    const storeCoordinates = await getStoreCoordinates({ postCode: postcode }, currentCountry);

    if (!storeCoordinates) {
      setEmptyError(true);
      setFilteredStores([]);
      return;
    }

    getStores({
      setStoreList: (stores) => {
        const filteredStores = stores.sort((store1, store2) => {
          const targetLocation = {
            lng: Number(storeCoordinates.longitude),
            lat: Number(storeCoordinates.latitude),
          };

          return getDistance(targetLocation, store1.coordinates) - getDistance(targetLocation, store2.coordinates);
        });

        const filterResult = filteredStores?.filter((el) => {
          return el.country === getCountryFullName(currentCountry);
        });

        if (filterResult.length === 0) {
          setEmptyError(true);
        }
        setFilteredStores(filterResult);
        setStoreList?.(filterResult);
      },
      skus: [sku],
      coordinates: storeCoordinates,
      setError: setEmptyError,
      setLoading: setLoadingStores,
    });
  };

  const handleChangeVariant = useCallback(
    async (value) => {
      if (preferredPostcode) {
        await handleFilterStores(preferredPostcode, setEmptyError, setFilteredStores, value);
      }
      onChangeVariant(value);
    },
    [setEmptyError, setFilteredStores, preferredPostcode, activeOptions],
  );

  const handleGetStoreList = useCallback(
    async (data) => {
      handlePostcodeChange(data.postCode);
      setPostCodeValue(data.postCode);
      if (activeOptions) {
        await handleFilterStores(data.postCode, setEmptyError, setFilteredStores, activeOptions.sku);
      }
    },
    [activeOptions],
  );

  useEffect(() => {
    if (preferredPostcode) {
      setFilteredStores([]);
      handleGetStoreList({ postCode: preferredPostcode });
    }
  }, [showDrawer]);

  return {
    emptyError,
    loadingStores,
    locationError,
    postCodeValue,
    hasAnyProductInCart,
    filteredStores,
    isMobile,
    loading,
    preferredPostcode,
    getUserPosition,
    handleChangeVariant,
    handleGetStoreList,
    setLocationError,
    setFilteredStores,
  };
};
